import type { ComponentType, FC } from 'react'
import {
  Stepper as MuiStepper,
  Step,
  StepLabel,
  StepIconProps as MuiStepIconProps,
  SvgIcon,
  SvgIconProps,
  useTheme,
  Typography,
  SxProps,
} from '@mui/material'
import { type Theme, tss } from '@dhl/theme'

const useStyles = tss.makeStyles<void, 'dotContainer' | 'dot'>()((theme: Theme, _, classes) => ({
  stepper: {
    border: 0,
    marginTop: theme.spacing(-1),
    '& .MuiStepLabel-label': {
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'center',
      },
    },
  },
  dot: {
    width: 8,
    height: 40,
    [theme.breakpoints.up('md')]: {
      width: 14,
      minHeight: 66,
    },
  },
  dotContainer: {
    textAlign: 'center',
  },
  icon: {
    filter: 'grayscale(100%)',
    width: 60,
    height: 40,
    [theme.breakpoints.up('md')]: {
      top: 18,
      position: 'relative',
      width: 87,
      height: 66,
    },
  },
  iconWithoutLabel: {
    [theme.breakpoints.down('sm')]: {
      maxWidth: 60,
      width: '100%',
      height: 40,
    },
    [theme.breakpoints.up('sm')]: {
      position: 'relative',
      width: 100,
      height: 66,
    },
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(-1),
      [`& .${classes.dot}`]: {
        marginTop: theme.spacing(2),
      },
    },
  },
  activeIcon: {
    '& .MuiSvgIcon-root': {
      filter: 'grayscale(0%)',
    },
    [`& .${classes.dotContainer}`]: {
      filter: 'grayscale(0%)',
    },
    height: 40,
    [theme.breakpoints.up('md')]: {
      height: 66,
    },
  },
}))

type StepIconProps = MuiStepIconProps & {
  allIcons?: boolean
}

const Dot = ({ completed, className }: Pick<StepIconProps, 'completed' | 'className'>) => {
  const { classes, cx } = useStyles()
  const theme = useTheme()

  return (
    <div className={cx(classes.dotContainer, className)}>
      <SvgIcon className={classes.dot} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle
          cx="50"
          cy="50"
          r="50"
          strokeWidth="0"
          fill={completed ? theme.palette.secondary.main : theme.palette.grey[400]}
        />
      </SvgIcon>
    </div>
  )
}

const StepIcon = ({ completed, active, icon, className, allIcons }: StepIconProps) => {
  const { classes, cx } = useStyles()
  const activeIcon = !allIcons || active || completed
  const showIcon = active || allIcons

  return (
    <div className={cx({ [classes.activeIcon]: activeIcon })}>
      {showIcon && icon}

      {!showIcon && <Dot className={className} completed={completed} />}
    </div>
  )
}

export type StepType = {
  icon: ComponentType<SvgIconProps>
  key: string
  label?: string
}

export type StepperProps = {
  activeStep: number
  steps: StepType[]
  className?: string
  allIcons?: boolean
  sx?: SxProps<Theme>
}

export const Stepper: FC<StepperProps> = ({ steps, activeStep, className, allIcons, sx }: StepperProps) => {
  const { classes, cx } = useStyles()

  return (
    <MuiStepper alternativeLabel activeStep={activeStep} className={cx(classes.stepper, className)} sx={sx}>
      {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
      {steps.map(({ key, icon: Icon, label }) => {
        const iconClass = label ? classes.icon : classes.iconWithoutLabel

        return (
          <Step key={key}>
            <StepLabel
              // eslint-disable-next-line react/no-unstable-nested-components
              StepIconComponent={(props) => <StepIcon className={iconClass} {...props} allIcons={allIcons} />}
              icon={<Icon className={iconClass} />}
            >
              {label && (
                <Typography variant="body2" translate="no">
                  {label}
                </Typography>
              )}
            </StepLabel>
          </Step>
        )
      })}
    </MuiStepper>
  )
}
